import formatNumberByLocale from '@utils/format-number-by-locale'
import classNames from 'classnames'
import map from 'lodash/map'
import React, {
  ChangeEvent,
  createRef,
  forwardRef,
  InputHTMLAttributes,
  MouseEvent,
  FocusEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useCallback
} from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import EnhancedBookAndActualQty from './EnhancedBookAndActualQty'
import EnhancedSizedQuantity from './EnhancedSizedQuantity'
import { ISize } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { useDoorQuantityHighlight } from '@components/UI-Components/ProductCard/ProductStatusHook'

interface QuantityCellProps {
  label?: string
  showBookCheckbox?: boolean
  isBooked?: boolean
  onIsBookedChanged?: InputHTMLAttributes<HTMLInputElement>['onChange']
  disabled: boolean
  isBookOpPending: boolean
  isHardExcluded: boolean
  doorProduct: any
  onTotalByDraftQuantityChange: InputHTMLAttributes<HTMLInputElement>['onChange']
  onTotalByDraftQuantityKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  onTotalByDraftQuantityFocus: InputHTMLAttributes<HTMLInputElement>['onFocus']
  onTotalByDraftQuantityMouseDown: InputHTMLAttributes<HTMLInputElement>['onMouseDown']
  autofocus?: boolean
  showSizeQuantitySummary: boolean
  sizes: ISize[]
  productHasSizes: boolean
  onSizeQuantityChange: (sizeId: string, e: ChangeEvent<HTMLInputElement>) => void
  onSizeQuantityFocus: (sizeId: string, e: FocusEvent<HTMLInputElement>) => void
  onSizeQuantityMouseDown: (sizeId: string, e: MouseEvent) => void
  onSizeQuantityKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  navigateToCell: (next: boolean) => void
  navigateToSize: (index: number) => void
  onBlurHandler?: () => void
  highlightDoors?: boolean
  doorToSizeQuantityDistributionToggle?: DoorToSizeQuantityDistributionToggle
  handleSplitOrRollupDoorsQty?: (isSplitOperation: boolean) => void
}

export interface DoorToSizeQuantityDistributionToggle {
  displaySplitOrRollUpIcon: boolean
  disabled: boolean
  hideSplitDownIcon: boolean
}

export interface QuantityCellRef {
  quantityInputRef: HTMLInputElement
  focusQtyInput: () => void
  focusSizeInput: (index) => void
}

const useStyles = makeStyles(theme => ({
  doorQuantities: {
    width: '100%'
  },
  sizeContainer: {
    width: '100%',
    padding: 10,
    height: 77,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  sizeQuantityLabel: {
    paddingBottom: 2
  },
  sizes: {
    width: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  input: {
    width: '100px!important',
    height: '20px!important',
    marginTop: '0px!important'
  },
  inactiveSize: {
    opacity: 0.5
  },
  highlightQtyCell: {
    background: 'rgba(255, 199, 0, 0.1)'
  }
}))

/**
 * Main List-View and Vertical-Detail door cell with size quantities
 */
const EnhancedQuantityCell = forwardRef<QuantityCellRef, QuantityCellProps>(
  (
    {
      label,
      showBookCheckbox = true,
      onTotalByDraftQuantityChange,
      disabled,
      isBookOpPending,
      isHardExcluded,
      isBooked,
      doorProduct,
      onIsBookedChanged,
      onTotalByDraftQuantityKeyDown,
      onTotalByDraftQuantityFocus,
      onTotalByDraftQuantityMouseDown,
      autofocus,
      showSizeQuantitySummary,
      sizes,
      productHasSizes,
      onSizeQuantityChange,
      onSizeQuantityKeyDown,
      onSizeQuantityMouseDown,
      onSizeQuantityFocus,
      navigateToCell,
      navigateToSize,
      onBlurHandler,
      highlightDoors,
      doorToSizeQuantityDistributionToggle,
      handleSplitOrRollupDoorsQty
    },
    ref
  ) => {
    const totalByDraftQuantityInputRef = useRef<HTMLInputElement>()
    const { t } = useTranslation()
    const classes = useStyles()

    const keyDownHandler = useCallback(
      (e, currSizeIndex) => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToSize(currSizeIndex - 1)
          } else {
            navigateToSize(currSizeIndex + 1)
          }
          e.preventDefault()
        }
        onSizeQuantityKeyDown?.(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const lastSizeKeyDownHandler = useCallback(
      (e, availableSizes) => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToSize(availableSizes.length - 1)
            ;(sizesEle[availableSizes.length - 1] as any)?.ref.current?.focus()
          } else {
            navigateToCell(true)
          }
          e.preventDefault()
        }
        onSizeQuantityKeyDown(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const sizesEle = useMemo(
      () => {
        if (sizes?.length) {
          const availableSizes = [...sizes]
          const availableSizesRef = availableSizes.map(() =>
            createRef<HTMLInputElement>()
          )
          const lastSize = availableSizes.pop()
          const lastSizeRef = availableSizesRef.pop()
          const isDisabled = disabled || !isBooked
          return availableSizes
            ?.map(({ id: sizeId, isInactive = false, ...rest }, currSizeIndex) => {
              return (
                <EnhancedSizedQuantity
                  key={sizeId}
                  id={sizeId}
                  ref={availableSizesRef[currSizeIndex]}
                  onChange={e => onSizeQuantityChange?.(sizeId, e)}
                  value={doorProduct?.doorOrderSizeQuantities?.[sizeId]?.quantity || 0}
                  disabled={isDisabled}
                  isReset={!isBooked}
                  className={classNames(classes.input, {
                    [classes.inactiveSize]: isInactive
                  })}
                  onKeyDown={e => keyDownHandler(e, currSizeIndex)}
                  onFocus={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityFocus?.(sizeId, e)
                  }}
                  onMouseDown={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityMouseDown?.(sizeId, e)
                  }}
                  onBlur={onBlurHandler}
                />
              )
            })
            .concat([
              <EnhancedSizedQuantity
                key={lastSize.id}
                id={lastSize.id}
                ref={lastSizeRef}
                onChange={e => onSizeQuantityChange?.(lastSize.id, e)}
                value={doorProduct?.doorOrderSizeQuantities?.[lastSize.id]?.quantity || 0}
                disabled={isDisabled}
                isReset={!isBooked}
                className={classNames(classes.input, {
                  [classes.inactiveSize]: lastSize.isInactive
                })}
                onKeyDown={e => lastSizeKeyDownHandler(e, availableSizes)}
                onFocus={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityFocus?.(lastSize.id, e)
                }}
                onMouseDown={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityMouseDown?.(lastSize.id, e)
                }}
                onBlur={onBlurHandler}
              />
            ])
        }
        return []
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sizes, disabled, isBooked, doorProduct]
    )

    useImperativeHandle(
      ref,
      () => ({
        quantityInputRef: totalByDraftQuantityInputRef.current,
        sizeElementRefs: map(sizesEle, 'ref.current'),
        focusQtyInput: () => {
          totalByDraftQuantityInputRef?.current?.focus()
        },
        focusSizeInput: index => {
          ;(sizesEle[index] as any)?.ref.current?.focus()
        }
      }),
      [sizesEle]
    )

    const doorQtyKeyDownHandler = useCallback(
      e => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToCell(false)
          } else {
            if (sizesEle?.length) {
              navigateToSize(0)
            } else {
              navigateToCell(true)
            }
          }
          e.preventDefault()
        }
        onTotalByDraftQuantityKeyDown?.(e)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sizesEle]
    )

    const doorQuantityValue = doorProduct?.doorOrderProductDoorQuantity || 0
    const doorSizeQuantity = doorProduct?.doorOrderProductSizeQuantity || 0
    const highlightInputBox = useDoorQuantityHighlight(
      isBooked,
      doorQuantityValue,
      doorSizeQuantity
    )

    const totalSizeQty = formatNumberByLocale(
      productHasSizes
        ? doorProduct?.doorOrderProductSizeQuantity || 0
        : doorProduct?.doorOrderProductDoorQuantity || 0
    )

    return (
      <div
        className={classNames(classes.doorQuantities, {
          [classes.highlightQtyCell]: highlightDoors
        })}
      >
        <div>
          <EnhancedBookAndActualQty
            label={label}
            showCheckbox={showBookCheckbox}
            value={doorProduct?.doorOrderProductDoorQuantity || 0}
            handleInputChange={onTotalByDraftQuantityChange}
            isBooked={isBooked}
            highlightDoors={highlightDoors}
            disabled={disabled}
            isBookOpPending={isBookOpPending}
            isHardExcluded={isHardExcluded}
            onChange={onIsBookedChanged}
            onKeyDown={e => doorQtyKeyDownHandler(e)}
            ref={totalByDraftQuantityInputRef}
            autoFocus={autofocus}
            onFocus={e => {
              const endIndex = e.target.value.length
              e.target.setSelectionRange(endIndex, endIndex)
              onTotalByDraftQuantityFocus(e)
            }}
            onMouseDown={onTotalByDraftQuantityMouseDown}
            onBlur={onBlurHandler}
            highlightQty={highlightInputBox}
            doorToSizeQuantityDistributionToggle={doorToSizeQuantityDistributionToggle}
            handleSplitOrRollupDoorsQty={isSplitOperation =>
              handleSplitOrRollupDoorsQty(isSplitOperation)
            }
          />
        </div>
        {showSizeQuantitySummary && (
          <div className={classes.sizeContainer}>
            <div className={classes.sizeQuantityLabel}>
              <Typography variant='body2'>
                {t('orderTable.cnfSizedQty', 'Size Quantity')}
              </Typography>
            </div>
            <Typography variant='h6'>{isBooked ? totalSizeQty : 0}</Typography>
          </div>
        )}
        <div className={classes.sizes}>{sizesEle}</div>
      </div>
    )
  }
)

export default EnhancedQuantityCell
